/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import {
  BadgeType,
  Asset,
  EntryPoint,
  Badge,
  ParentalRatingOptionsType,
  Icons,
  translate,
  Program,
  RecordingsUtils,
  RecordingStatus,
  TimeUtils,
  TvAssetUtils,
  RelativeTime,
  ContentTagsUtils,
  Recording,
  Folder,
  isEntitlement,
  isSubscriptionEntitlement,
  isCollectionEntitlement,
  Vod,
  FolderType,
  AssetUtils,
} from '@wit/vtv-x-foundation-lib';
import store from 'store/store';
import appTranslate from 'services/i18n/translate';
import ParentalControl from '../ParentalControl';
import { Selectors as SeriesRecordingsSelectors } from '../../../store/ducks/recordings/seriesRecordings';
import { Selectors as SingleRecordingsSelectors } from '../../../store/ducks/recordings/singleRecordings';
import { Selectors as WishlistSelectors } from '../../../store/ducks/wishlist';
import BadgeEngine from './BadgeEngine';
import Colors from '../../../../ui/common/Colors';
import PIOptionsBadgeEngine from './PIOptionsBadgeEngine';

export default class RailBadgeEngine extends BadgeEngine {
  static getBadgesListForRailAsset(
    asset: Asset,
    railBadgesPriority: Array<BadgeType[]>,
    entryPoint?: EntryPoint
  ): Asset {
    const badgesList: Badge[] = [];

    for (let i = 0; i < railBadgesPriority.length; i += 1) {
      const slotBadgesPriority = railBadgesPriority[i];

      for (let ii = 0; ii < slotBadgesPriority.length; ii += 1) {
        const badge = RailBadgeEngine.determineBadge(slotBadgesPriority[ii], asset, entryPoint);

        if (badge) {
          badgesList.push(badge);
          break;
        }
      }
    }

    return {
      ...asset,
      badges: badgesList,
    };
  }

  static getAssetsWithBadgesListFromRail(
    assets: Asset[],
    railBadgesPriority: Array<BadgeType[]>,
    entryPoint?: EntryPoint
  ): Asset[] {
    const result: Asset[] = [];
    assets.forEach((asset) =>
      result.push(RailBadgeEngine.getBadgesListForRailAsset(asset, railBadgesPriority, entryPoint))
    );
    return result;
  }

  private static determineBadge(type: BadgeType, asset: Asset, entryPoint?: EntryPoint): Badge | undefined {
    switch (type) {
      case BadgeType.LOCK:
        return RailBadgeEngine.determineLockBadge(asset);
      case BadgeType.PLAY:
        return RailBadgeEngine.determinePlayBadge(asset, entryPoint);
      case BadgeType.PURCHASE:
        return RailBadgeEngine.determinePurchaseBadge(asset);
      case BadgeType.SERIES_SCHEDULED_RECORDING:
      case BadgeType.SERIES_RECORDING:
      case BadgeType.LIVE_SERIES_RECORDING:
        return RailBadgeEngine.determineSeriesRecordingBadge(asset);
      case BadgeType.SINGLE_SCHEDULED_RECORDING:
      case BadgeType.SINGLE_RECORDING:
      case BadgeType.LIVE_RECORDING:
        return RailBadgeEngine.determineSingleRecordingBadge(asset);
      case BadgeType.EXPIRY_CLOCK:
        return RailBadgeEngine.determineExpiryClockBadge(asset);
      case BadgeType.PROTECTED_RECORDING:
        return RailBadgeEngine.determineProtectedRecordingBadge(asset);
      case BadgeType.WISHLIST_ON: {
        const badge = RailBadgeEngine.determineWishlistBadge(asset);
        return badge.type === BadgeType.WISHLIST_ON ? badge : undefined;
      }
      case BadgeType.AIRS_ON:
        return RailBadgeEngine.determineAirsOnBadge(asset);
      default:
        return undefined;
    }
  }

  private static determineLockBadge(asset: Asset): Badge | undefined {
    if (ParentalControl.UI.getAssetParentalState(asset) === ParentalRatingOptionsType.PIN) {
      return {
        type: BadgeType.LOCK,
        icon: Icons.LOCK,
        label: translate['badge.rails.lock'],
      };
    }
    return undefined;
  }

  private static handleEpgForDeterminePlayBadge(program: Program, entryPoint?: EntryPoint): string[] {
    const label: string[] = [];

    const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
    const recording = recordings.find((e) => e.internalId === program.internalId);
    if (recording && RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.COMPLETED) {
      label.push(
        translate
          .formatString(
            translate['badge.rails.play.recorded'],
            RailBadgeEngine.toTimeFormat(TimeUtils.toUnixEpoch(recording.actualStartTime) * 1000)
          )
          .toString()
      );

      const expiringInLabel = RailBadgeEngine.getExpiringInLabel(recording, translate['badge.rails.play.expiresIn']);
      if (expiringInLabel) {
        label.push(expiringInLabel);
      }
    } else if (
      TvAssetUtils.getRelativeTime(program) === RelativeTime.PAST &&
      ContentTagsUtils.isPlaybackAllowedForCatchupAsset(program)
    ) {
      label.push(
        translate
          .formatString(
            translate['badge.rails.play.aired'],
            RailBadgeEngine.toTimeFormat(TimeUtils.toUnixEpoch(program.startDate) * 1000)
          )
          .toString()
      );

      const expiringInLabel = RailBadgeEngine.getExpiringInLabel(program, translate['badge.rails.play.expiresIn']);
      if (expiringInLabel) {
        label.push(expiringInLabel);
      }
    } else if (
      TvAssetUtils.getRelativeTime(program) === RelativeTime.LIVE &&
      ContentTagsUtils.isPlaybackAllowedForLiveAsset(program)
    ) {
      if (ContentTagsUtils.isResartAllowedForEpgAsset(program)) {
        label.push(translate['badge.rails.play.restartAvailable']);
      } else {
        label.push(translate['badge.rails.play.airingNow']);
      }

      if (entryPoint !== EntryPoint.SEASONS) {
        label.push(
          translate
            .formatString(
              translate['badge.rails.play.remainingTime'],
              RailBadgeEngine.getRemainingTimeLabel(TimeUtils.diff(TimeUtils.now(), program.endDate))
            )
            .toString()
        );
      }
    }

    return label;
  }

  private static handleRecordingForDeterminePlayBadge(recording: Recording): string[] {
    const label: string[] = [];

    label.push(
      translate
        .formatString(
          translate['badge.rails.play.recorded'],
          RailBadgeEngine.toTimeFormat(TimeUtils.toUnixEpoch(recording.actualStartTime) * 1000)
        )
        .toString()
    );

    const expiringInLabel = RailBadgeEngine.getExpiringInLabel(recording, translate['badge.rails.play.expiresIn']);
    if (expiringInLabel) {
      label.push(expiringInLabel);
    }

    return label;
  }

  private static handleFolderForDeterminePlayBadge(folder: Folder): string[] {
    const label: string[] = [];

    const entitlement = RailBadgeEngine.getAssetEntitled(folder);
    if (entitlement && isEntitlement(entitlement) && TimeUtils.isAfterNow(entitlement.endDate)) {
      if (isSubscriptionEntitlement(entitlement)) {
        label.push(translate['badge.rails.play.subscription.subscribed']);
        if (entitlement.isRenewable) {
          label.push(translate['badge.rails.play.subscription.renewsMonthly']);
        } else {
          const expiringInLabel = RailBadgeEngine.getExpiringInLabel(folder, translate['badge.rails.play.expiresIn']);
          if (expiringInLabel) {
            label.push(expiringInLabel);
          }
        }
      } else if (isCollectionEntitlement(entitlement)) {
        if (RailBadgeEngine.isEntitlementRented(entitlement)) {
          const expiringInLabel = RailBadgeEngine.getExpiringInLabel(folder, translate['badge.rails.play.expiresIn']);
          if (expiringInLabel) {
            label.push(expiringInLabel);
          }
        } else {
          label.push(translate['badge.rails.play.boxset.purchased']);
        }
      }
    }

    return label;
  }

  private static handleVodForDeterminePlayBadge(vod: Vod): string[] {
    const label: string[] = [];

    const entitlement = RailBadgeEngine.getAssetEntitled(vod);
    if (entitlement && isEntitlement(entitlement) && TimeUtils.isAfterNow(entitlement.endDate)) {
      if (RailBadgeEngine.isEntitlementRented(entitlement)) {
        const expiringInLabel = RailBadgeEngine.getExpiringInLabel(vod, translate['badge.rails.play.expiresIn']);
        if (expiringInLabel) {
          label.push(expiringInLabel);
        }
      } else {
        label.push(translate['badge.rails.play.tvod.purchased']);
      }
    }

    return label;
  }

  private static determinePlayBadge(asset: Asset, entryPoint?: EntryPoint): Badge | undefined {
    if (AssetUtils.isSeries(asset) || (AssetUtils.isFolder(asset) && asset.folderType === FolderType.SIMPLE)) {
      return undefined;
    }

    if (RailBadgeEngine.getAssetEntitled(asset)) {
      let label: string[] = [];

      if (AssetUtils.isEpg(asset)) {
        label = RailBadgeEngine.handleEpgForDeterminePlayBadge(asset, entryPoint);
      } else if (
        AssetUtils.isRecording(asset) &&
        RecordingsUtils.getRecordingStatus(asset) === RecordingStatus.COMPLETED
      ) {
        label = RailBadgeEngine.handleRecordingForDeterminePlayBadge(asset);
      } else if (AssetUtils.isFolder(asset)) {
        label = RailBadgeEngine.handleFolderForDeterminePlayBadge(asset);
      } else if (AssetUtils.isVod(asset) && ContentTagsUtils.isPlaybackAllowedForVodAsset(asset)) {
        label = RailBadgeEngine.handleVodForDeterminePlayBadge(asset);
      }

      if (label.length > 0) {
        return {
          type: BadgeType.PLAY,
          icon: Icons.PLAY_CIRCLE,
          label,
        };
      }
    }

    return undefined;
  }

  private static determinePurchaseBadge(asset: Asset): Badge | undefined {
    const label: string[] = [];

    if (RailBadgeEngine.isPurchasedBadgeEnabled() && !RailBadgeEngine.getAssetEntitled(asset)) {
      if (AssetUtils.isVod(asset) && RailBadgeEngine.isPurchasedBadgeEnabledForTVOD()) {
        label.push(translate['badge.rails.purchased.tvod.availableToPurchase']);
      } else if (AssetUtils.isFolder(asset)) {
        if (asset.folderType === FolderType.SUBSCRIPTION && RailBadgeEngine.isPurchasedBadgeEnabledForSVOD()) {
          label.push(translate['badge.rails.purchased.subscription.availableToSubscribe']);
        } else if (asset.folderType === FolderType.BUNDLE && RailBadgeEngine.isPurchasedBadgeEnabledForBoxset()) {
          label.push(translate['badge.rails.purchased.boxset.availableToPurchase']);
        }
      }
    }

    if (label.length > 0) {
      return {
        type: BadgeType.PURCHASE,
        icon: RailBadgeEngine.getCurrencyIcon(),
        label,
      };
    }

    return undefined;
  }

  private static determineSeriesRecordingBadge(asset: Asset): Badge | undefined {
    if (AssetUtils.isTvAsset(asset)) {
      const seriesRecordings = SeriesRecordingsSelectors.getRecordings(store().getState());
      const serieRecording = seriesRecordings.find((e) => e.seriesId === asset.seriesId);
      if (serieRecording) {
        const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
        const recording = recordings.find((e) => e.internalId === asset.internalId);

        if (recording) {
          if (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.ON_GOING) {
            return {
              type: BadgeType.LIVE_SERIES_RECORDING,
              icon: Icons.RECORDING_SERIES,
              color: Colors.brand,
              label: [
                translate['badge.rails.liveSeriesRecording.recordingNow'],
                translate
                  .formatString(
                    translate['badge.rails.liveSeriesRecording.remainingTime'],
                    RailBadgeEngine.getRemainingTimeLabel(TimeUtils.diff(TimeUtils.now(), recording.actualEndTime))
                  )
                  .toString(),
              ],
            };
          }

          if (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.SCHEDULED) {
            return {
              type: BadgeType.SERIES_SCHEDULED_RECORDING,
              icon: Icons.RECORDING_SERIES_SCHEDULED,
              label: [
                translate['badge.rails.scheduledSeriesRecording.scheduledSeries'],
                TimeUtils.toBadgeDateTimeFormat(TimeUtils.toUnixEpoch(recording.startDate) * 1000),
              ],
            };
          }
        }
      }
    }

    return undefined;
  }

  private static determineSingleRecordingBadge(asset: Asset): Badge | undefined {
    if (AssetUtils.isTvAsset(asset)) {
      const recordings = SingleRecordingsSelectors.getRecordings(store().getState());
      const recording = recordings.find((e) => e.internalId === asset.internalId);

      if (recording) {
        if (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.ON_GOING) {
          return {
            type: BadgeType.LIVE_RECORDING,
            icon: Icons.RECORDING_SINGLE,
            color: Colors.brand,
            label: [
              translate['badge.rails.liveRecording.recordingNow'],
              translate
                .formatString(
                  translate['badge.rails.liveRecording.remainingTime'],
                  RailBadgeEngine.getRemainingTimeLabel(TimeUtils.diff(TimeUtils.now(), recording.actualEndTime))
                )
                .toString(),
            ],
          };
        }

        if (RecordingsUtils.getRecordingStatus(recording) === RecordingStatus.SCHEDULED) {
          return {
            type: BadgeType.SINGLE_SCHEDULED_RECORDING,
            icon: Icons.RECORDING_SINGLE_SCHEDULED,
            label: [
              translate['badge.rails.scheduledRecording.scheduledRecording'],
              TimeUtils.toBadgeDateTimeFormat(TimeUtils.toUnixEpoch(recording.startDate) * 1000),
            ],
          };
        }
      }
    }

    return undefined;
  }

  static determineExpiryClockBadge(asset: Readonly<Asset>): Badge | undefined {
    const assetEntitlement = PIOptionsBadgeEngine.getAssetEntitled(asset);
    if (assetEntitlement) {
      if (AssetUtils.isRecording(asset)) {
        const expiringInLabel = PIOptionsBadgeEngine.getExpiringInLabel(asset, translate['badge.pi.play.expiresIn']);
        if (expiringInLabel) {
          return PIOptionsBadgeEngine.buildBadge(BadgeType.EXPIRY_CLOCK, expiringInLabel);
        }
      }

      if (
        AssetUtils.isVod(asset) &&
        isEntitlement(assetEntitlement) &&
        PIOptionsBadgeEngine.isEntitlementRented(assetEntitlement)
      ) {
        const expiringInLabel = PIOptionsBadgeEngine.getExpiringInLabel(asset, translate['badge.pi.play.expiresIn']);
        if (expiringInLabel) {
          return PIOptionsBadgeEngine.buildBadge(BadgeType.EXPIRY_CLOCK, expiringInLabel);
        }
      }
    }
    return undefined;
  }

  static determineWishlistBadge(asset: Readonly<Asset>): Badge {
    const isWishlisted = WishlistSelectors.isAssetWishlisted(store().getState(), asset);

    if (isWishlisted) {
      return PIOptionsBadgeEngine.buildBadge(BadgeType.WISHLIST_ON, translate['badge.pi.wishlist.addedToWishlist']);
    }
    return PIOptionsBadgeEngine.buildBadge(BadgeType.WISHLIST_OFF, translate['badge.pi.wishlist.addToWishlist']);
  }

  static determineProtectedRecordingBadge(asset: Readonly<Asset>): Badge | undefined {
    if (AssetUtils.isRecording(asset) && asset.isAssetProtected) {
      return PIOptionsBadgeEngine.buildBadge(
        BadgeType.PROTECTED_RECORDING,
        translate['badge.pi.protectedRecording.recordingProtected']
      );
    }
    return undefined;
  }

  static determineAirsOnBadge(asset: Readonly<Asset>): Badge | undefined {
    if (
      !(
        AssetUtils.isTvAsset(asset) &&
        (TvAssetUtils.getRelativeTime(asset) === RelativeTime.LIVE ||
          TvAssetUtils.getRelativeTime(asset) === RelativeTime.FUTURE)
      )
    ) {
      return undefined;
    }

    const nextAiringDateEpoch =
      asset.startDate && TimeUtils.isAfterNow(asset.startDate) && TimeUtils.toEpoch(asset.startDate);
    const nextAiringLabel = nextAiringDateEpoch
      ? `${asset.startTime || ''} ${TimeUtils.getShortWeekDay(nextAiringDateEpoch)} ${TimeUtils.getDay(
          nextAiringDateEpoch
        )} ${TimeUtils.getShortMonth(nextAiringDateEpoch)}`
      : '-';
    const labelText = nextAiringDateEpoch
      ? appTranslate.formatString(appTranslate.badgesProgramInfoAirsOn, nextAiringLabel).toString()
      : translate['badge.pi.play.airingNow'].toString();

    return {
      type: BadgeType.AIRS_ON,
      icon: Icons.AIRS_ON,
      label: labelText,
    };
  }
}
